import type { ReactNode } from 'react';
import { createContext, use, useCallback, useMemo, useRef } from 'react';
import type { AppBundle } from '../utils/app-bundles';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

interface AppBundleData {
  initialized: boolean;
  loaded: boolean;
  appWrapper: HTMLDivElement;
  cleanup: () => void;
  loadPromise: Promise<void>;
  loadStartedAt?: number;
}

interface BackgroundAppBundleContextType {
  getBackgroundAppData: (appId: AppBundle) => AppBundleData;
  setBackgroundAppData: (appId: AppBundle, data: Partial<AppBundleData>) => void;
}

const BackgroundAppBundleContext = createContext<BackgroundAppBundleContextType | undefined>(undefined);
const missingContextErrorMessage = 'useBackgroundAppBundle must be used within an BackgroundAppBundleContextProvider';

export function useBackgroundAppBundle(appId: AppBundle) {
  const context = use(BackgroundAppBundleContext);
  const getData = useCallback(() => {
    if (context === undefined) {
      throw new Error(missingContextErrorMessage);
    }

    return context.getBackgroundAppData(appId);
  }, [appId, context]);
  const setData = useCallback(
    (data: Partial<AppBundleData>) => {
      if (context === undefined) {
        throw new Error(missingContextErrorMessage);
      }

      context.setBackgroundAppData(appId, data);
    },
    [appId, context],
  );

  if (context === undefined) {
    throw new Error(missingContextErrorMessage);
  }

  return { getData, setData };
}

/** This context is used to store data about app bundles that are loaded in the background.
 * More info about this behavior can be found in the AppBundleLoader component. */

type BackgroundAppBundleContextProviderProps = { children: ReactNode };

export function BackgroundAppBundleContextProvider({ children }: BackgroundAppBundleContextProviderProps) {
  const appDataMapRef = useRef<Record<AppBundle, AppBundleData>>({} as Record<AppBundle, AppBundleData>);

  const getBackgroundAppData = useCallback((appId: AppBundle) => {
    return appDataMapRef.current[appId];
  }, []);

  const setBackgroundAppData = useCallback((appId: AppBundle, data: Partial<AppBundleData>) => {
    appDataMapRef.current = {
      ...appDataMapRef.current,
      [appId]: {
        ...appDataMapRef.current[appId],
        ...data,
      },
    };
  }, []);
  const value = useMemo(
    () => ({ getBackgroundAppData, setBackgroundAppData }),
    [getBackgroundAppData, setBackgroundAppData],
  );

  /** Cleans up app wrappers and app data when this entire context is unmounted
   * (not just when an app bundle is unmounted) */
  useMountEffect(function cleanupOnUnmount() {
    return () => {
      for (const appData of Object.values(appDataMapRef.current)) {
        if (appData.appWrapper != null) {
          appData.appWrapper.remove();
        }
        if (appData.cleanup != null) {
          appData.cleanup();
        }
      }
    };
  });

  return <BackgroundAppBundleContext.Provider value={value}>{children}</BackgroundAppBundleContext.Provider>;
}
